import { LayoutGroup } from "framer-motion";
import { Outlet } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { NavigationSectionConfig } from "@/features/Navigation/types/Navigation.types";
import {
    SidebarHeader,
    SidebarPortal,
} from "@/features/Navigation/components/SidebarPortal";
import { ContextMenu } from "@/features/Navigation/components/ContextMenu";
import { useRouter } from "@/utils";
import { atom, useAtom, useSetAtom } from "jotai";
import { showParentHeaderAtom } from "@/features/Navigation/state";
import ReturnHeader from "@/features/Navigation/components/ReturnHeader";
import { CompanyItem } from "@/features/Navigation/components/CompanyItem";
import SearchBar from "@/features/Navigation/components/SearchBar";
import { useCompany } from "@/apps/CompanyApp/hooks/useCompany";
import clsx from "clsx";
import { useMediaQuery } from "@/ui/hooks/useMediaQuery";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useInfiniteScrollCompanies } from "@/entities/Company/useInfiniteScrollCompanies";
import { Spinner } from "@/ui/atoms/Spinner";

export const showCompanyListAtom = atom(false);

export const subsections: NavigationSectionConfig[] = [
    // {
    //     label: "Company Overview",
    //     getHref: (params) => `/beta/companies/${params.companyId}`,
    //     regex: "^/beta/companies/[^/]+/?$",
    // },
    {
        label: "Investment Memo",
        getHref: (params) =>
            `/beta/companies/${params.companyId}/investment-memo`,
        regex: "^/beta/companies/[^/]+/investment-memo/?$",
    },
    {
        label: "Connectors",
        getHref: (params) => `/beta/companies/${params.companyId}/connectors`,
        regex: "^/beta/companies/[^/]+/connectors(/.*)?/?$",
    },
    {
        label: "Uploads",
        getHref: (params) => `/beta/companies/${params.companyId}/uploads`,
        regex: "^/beta/companies/[^/]+/uploads(/.*)?/?$",
    },
    {
        label: "Labeling",
        getHref: (params) => `/beta/companies/${params.companyId}/labeling`,
        regex: "^/beta/companies/[^/]+/labeling(/.*)?/?$",
    },
    {
        label: "Opportunities",
        getHref: (params) =>
            `/beta/companies/${params.companyId}/opportunities`,
        regex: "^/beta/companies/[^/]+/opportunities(/.*)?/?$",
    },
    {
        label: "Metric Lab",
        getHref: (params) => `/beta/companies/${params.companyId}/metrics`,
        regex: "^/beta/companies/[^/]+/metrics(/.*)?/?$",
    },
];

export function CompaniesLayout() {
    const { company } = useCompany();
    const router = useRouter();
    const [showCompanyList, setShowCompanyList] = useAtom(showCompanyListAtom);
    const setShowParentHeader = useSetAtom(showParentHeaderAtom);
    const [searchTerm, setSearchTerm] = useState("");

    const {
        companies,
        loadNextPage,
        hasNextPage,
        isLoading,
        isLoadingMore,
        error,
    } = useInfiniteScrollCompanies(searchTerm);

    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: companies.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 60,
        overscan: 10,
    });

    const handleScroll = useCallback(() => {
        const virtualItems = virtualizer.getVirtualItems();
        if (virtualItems.length === 0) return;

        const lastItem = virtualItems[virtualItems.length - 1];
        if (
            (lastItem.index >= companies.length - 10,
            hasNextPage && !isLoadingMore)
        ) {
            loadNextPage();
        }
    }, [
        virtualizer,
        companies.length,
        hasNextPage,
        isLoadingMore,
        loadNextPage,
    ]);

    const isIndex = router.isPathMatch("^/beta/companies(/)?$");
    const matchingConfig = subsections.find((section) =>
        router.isPathMatch(section.regex),
    );
    const isMobile = useMediaQuery("(max-width: 640px)");

    useEffect(() => {
        if (isIndex && !showCompanyList) {
            setShowCompanyList(true);
        }
    }, [router.pathname, showCompanyList]);

    useEffect(() => {
        if (isIndex || showCompanyList) {
            setShowParentHeader(true);
        } else {
            setShowParentHeader(false);
        }
    }, [router.pathname, showCompanyList]);

    return (
        <LayoutGroup>
            <SidebarHeader>
                {!showCompanyList && (
                    <div className="mb-3 sm:px-3">
                        <ReturnHeader
                            isMobile={isMobile}
                            backLink={{
                                label: "Companies",
                                onClick: () => setShowCompanyList(true),
                            }}
                        />
                        <ContextMenu
                            title={company?.name || "-"}
                            navigationConfig={subsections}
                        />
                    </div>
                )}
            </SidebarHeader>
            {showCompanyList && (
                <SidebarPortal>
                    <SidebarHeader>
                        <SearchBar
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder="Search..."
                        />
                    </SidebarHeader>
                    <div
                        ref={parentRef}
                        className={clsx(
                            "mt-6",
                            "flex flex-col",
                            "hide-scrollbar overflow-y-auto",
                            "min-h-0 flex-1",
                        )}
                        style={{
                            contain: "strict",
                        }}
                        onScroll={handleScroll}
                    >
                        {isLoading && (
                            <div className="flex justify-center py-8">
                                <Spinner />
                            </div>
                        )}

                        {!isLoading && companies.length > 0 && (
                            <div
                                style={{
                                    height: `${virtualizer.getTotalSize()}px`,
                                    width: "100%",
                                    position: "relative",
                                }}
                            >
                                {virtualizer
                                    .getVirtualItems()
                                    .map((virtualItem) => {
                                        const company =
                                            companies[virtualItem.index];

                                        return (
                                            <div
                                                key={virtualItem.key}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: `${virtualItem.size}px`,
                                                    transform: `translateY(${virtualItem.start}px)`,
                                                }}
                                            >
                                                {company && (
                                                    <CompanyItem
                                                        company={company}
                                                        onClick={() => {
                                                            setShowCompanyList(
                                                                false,
                                                            );
                                                            router.navigate(
                                                                matchingConfig?.getHref(
                                                                    {
                                                                        companyId:
                                                                            company.uuid,
                                                                    },
                                                                ) ||
                                                                    `/beta/companies/${company.uuid}`,
                                                            );
                                                        }}
                                                        active={router.isPathMatch(
                                                            `/beta/companies/${company.uuid}`,
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        )}

                        {!isLoading &&
                            !error &&
                            companies.length === 0 &&
                            searchTerm && (
                                <p className="body-s subdued py-8 text-center">
                                    No companies found for &quot;{searchTerm}
                                    &quot;
                                </p>
                            )}

                        {error && (
                            <p className="body-s subdued py-8 text-center">
                                Error loading companies. Please try again.
                            </p>
                        )}
                    </div>
                </SidebarPortal>
            )}
            <Outlet />
        </LayoutGroup>
    );
}
